import { CartAddEvent } from '@theme/events';
import { interpolate } from './template-interpolation.js';

/**
 * Custom Web Component for handling a Product Bundle / Product Set.
 *
 * Features:
 * - Tab switching between product groups
 * - Selecting / deselecting product variants
 * - Tracking selected variants
 * - Updating threshold progress
 * - Updating bundle total price
 * - Creating bundle and adding items to cart
 * - Product detail popup for variant selection
 *
 * @extends HTMLElement
 */
class ProductSet extends HTMLElement {

  /** @type {Array} Stores selected variants */
  selectedVariants = [];

  /** @type {NodeList} Tab buttons */
  tabButtons;

  /** @type {NodeList} Select buttons */
  selectButtons;

  /** @type {HTMLElement} Threshold wrapper */
  thresholdContainer;

  /** @type {HTMLElement} Threshold text element */
  thresholdText;

  /** @type {number} Maximum threshold */
  threshold = 0;

  /** @type {HTMLElement} Bundle total price element */
  setPrice;

  /** @type {HTMLElement} Add bundle to cart button */
  addToCartButton;

  /** @type {HTMLElement} Popup overlay element */
  popupOverlay;

  /** @type {HTMLElement} Popup body container */
  popupBody;

  /** @type {HTMLElement} Popup close button */
  popupCloseBtn;

  /** @type {HTMLElement} The select button that triggered the popup */
  currentSelectButton;

  constructor() {
    super();

    this.selectedVariants = [];

    this.handleTabClick = this.#handleTabClick.bind(this);
    this.handleSelectButtonClick = this.#handleSelectButtonClick.bind(this);
    this.handleAddToCartClick = this.#handleAddToCartClick.bind(this);
    this.handleVariantChange = this.#handleVariantChange.bind(this);
    this.handleProductItemClick = this.#handleProductItemClick.bind(this);
    this.handlePopupClose = this.closePopup.bind(this);
    this.handleOverlayClick = this.#handleOverlayClick.bind(this);
    this.handleKeyDown = this.#handleKeyDown.bind(this);
  }

  connectedCallback() {

    this.tabButtons = this.querySelectorAll('.product-set-title');

    this.tabButtons.forEach(element => {
      element.addEventListener('click', this.handleTabClick);
    });

    this.selectButtons = this.querySelectorAll('.product-set-select-button');

    this.selectButtons.forEach(element => {
      element.addEventListener('click', this.handleSelectButtonClick);
    });

    this.thresholdContainer = this.querySelector('.promo-threshold-wrapper');
    this.thresholdText = this.querySelector('.promo-threshold-value');
    this.thresholdMessage = this.querySelector('.promo-threshold-message');
    this.discountLabel = this.querySelector('.discount-label');
    this.messageTemplate = this.thresholdMessage?.dataset.messageTemplate || '';

    const initialSet = window.productSet?.[0];
    if (initialSet) {
      this.threshold = Number(initialSet.promoThreshold || 0);
      this.messageTemplate = initialSet.promoValue || this.messageTemplate;
      this.discountLabelTemplate = initialSet.discountLabel || '';
      this.successMessage = initialSet.successMessage || '';
    } else {
      this.threshold = Number(this.thresholdContainer?.dataset.threshold || 0);
      this.successMessage = '';
    }

    this.setPrice = this.querySelector('.total-set-price-money');

    this.addToCartButton = this.querySelector('.add-to-cart-set-button');
    this.selectItemsText = this.querySelector('.select-items-text__content');
    this.selectTextTemplate = this.selectItemsText?.dataset.selectTemplate || '';

    this.addToCartButton?.addEventListener('click', this.handleAddToCartClick);

    this.addEventListener('variant:update', this.handleVariantChange, true);

    /** Popup elements */
    this.popupOverlay = this.querySelector('[data-popup-overlay]');
    this.popupBody = this.querySelector('[data-popup-body]');
    this.popupCloseBtn = this.querySelector('[data-popup-close]');

    this.popupCloseBtn?.addEventListener('click', this.handlePopupClose);
    this.popupOverlay?.addEventListener('click', this.handleOverlayClick);
    document.addEventListener('keydown', this.handleKeyDown);

    /** Prevent product-card native navigation inside the product set */
    this.querySelectorAll('.product-set-item product-card').forEach(card => {
      card.setAttribute('data-no-navigation', '');
    });

    /** Product card click handlers */
    this.productSetItems = this.querySelectorAll('.product-set-item');
    this.productSetItems.forEach(item => {
      item.addEventListener('click', this.handleProductItemClick);
    });

    this.updateAddToCartButton();
  }

  disconnectedCallback() {

    this.tabButtons.forEach(element => {
      element.removeEventListener('click', this.handleTabClick);
    });

    this.selectButtons.forEach(element => {
      element.removeEventListener('click', this.handleSelectButtonClick);
    });

    this.addToCartButton?.removeEventListener('click', this.handleAddToCartClick);
    this.removeEventListener('variant:update', this.handleVariantChange, true);

    this.popupCloseBtn?.removeEventListener('click', this.handlePopupClose);
    this.popupOverlay?.removeEventListener('click', this.handleOverlayClick);
    document.removeEventListener('keydown', this.handleKeyDown);

    this.productSetItems?.forEach(item => {
      item.removeEventListener('click', this.handleProductItemClick);
    });
  }

  /**
   * Handles tab switching between product sets
   */
  #handleTabClick(event) {

    let that = event.currentTarget;

    if (that.classList.contains('active')) return;

    let itemContent = this.querySelectorAll('.product-set-items-content');
    let tabButtons = this.querySelectorAll('.product-set-title');

    tabButtons.forEach(el => el.classList.remove('active'));
    itemContent.forEach(el => el.classList.remove('active'));

    that.classList.add('active');

    let setTitle = that.dataset.setTitle;

    this.querySelector(`.product-set-items-content[data-set-title="${setTitle}"]`)
      ?.classList.add('active');

    const activeSet = window.productSet?.find(s => s.setTitle === setTitle);
    if (activeSet) {
      this.threshold = Number(activeSet.promoThreshold || 0);
      this.messageTemplate = activeSet.promoValue || '';
      this.discountLabelTemplate = activeSet.discountLabel || '';
      this.successMessage = activeSet.successMessage || '';

      if (this.thresholdContainer) {
        this.thresholdContainer.dataset.threshold = this.threshold;
      }

      this.#rebuildProgressBars(this.threshold);
      this.selectedVariants = [];
      this.#resetSelectButtons();
      this.updateThreshold();
      this.updateBundlePrice();
      this.updateAddToCartButton();
    }
  }

  #rebuildProgressBars(count) {
    const wrapper = this.querySelector('.threshold-wrapper');
    if (!wrapper) return;

    wrapper.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const bar = document.createElement('div');
      bar.classList.add('threshold-bar');
      wrapper.appendChild(bar);
    }
  }

  #resetSelectButtons() {
    this.selectButtons.forEach(btn => {
      btn.classList.remove('selected-item', 'button-primary-option-2');
      btn.classList.add('button-secondary');
    });
  }

  /**
   * Handles swatch variant change within a product card.
   * Updates the select button's variant ID and syncs selectedVariants.
   * Opens the popup on variant change (unless event is from popup).
   */
  #handleVariantChange(event) {
    if (event.target.closest('.product-set-popup-overlay')) return;

    const productSetItem = event.target.closest('.product-set-item');
    if (!productSetItem) return;

    const selectButton = productSetItem.querySelector('.product-set-select-button');
    if (!selectButton) return;

    const newVariant = event.detail?.resource;
    if (!newVariant?.id) return;

    const oldVariantId = Number(selectButton.dataset.variantId);
    const newVariantId = Number(newVariant.id);

    selectButton.dataset.variantId = newVariantId;

    if (newVariant.available === false) {
      selectButton.setAttribute('disabled', 'true');
    } else {
      selectButton.removeAttribute('disabled');
    }

    const cardImage = productSetItem.querySelector('.product-set-image__img');
    if (cardImage && newVariant.featured_image?.src) {
      cardImage.src = newVariant.featured_image.src;
      cardImage.srcset = '';
    }

    if (this.selectedVariants.some(v => v.variantId === oldVariantId)) {
      this.removeVariant(oldVariantId);
      selectButton.classList.remove('selected-item', 'button-primary-option-2');
      selectButton.classList.add('button-secondary');
    }

    const productHandle = selectButton.dataset.productHandle;
    if (productHandle) {
      this.openPopup(productHandle, selectButton);
    }
  }

  /**
   * Handles click on product card area (image, title, price).
   * Prevents default link navigation and opens the popup.
   */
  #handleProductItemClick(event) {
    if (event.target.closest('.product-set-select-button')) return;
    if (event.target.closest('product-swatches')) return;
    if (event.target.closest('swatches-variant-picker-component')) return;
    if (event.target.closest('.variant-option')) return;

    event.preventDefault();

    const productSetItem = event.currentTarget;
    const btn = productSetItem.querySelector('.product-set-select-button');
    if (!btn) return;

    const productHandle = btn.dataset.productHandle;
    if (productHandle) {
      this.openPopup(productHandle, btn);
    }
  }

  /**
   * Handles select / deselect button click.
   * If not selected: opens popup for variant confirmation.
   * If already selected: deselects the variant.
   */
  #handleSelectButtonClick(event) {

    let that = event.currentTarget;
    let variantId = Number(that?.dataset.variantId);

    if (this.selectedVariants.some(v => v.variantId === variantId)) {
      this.removeVariant(variantId);

      that?.classList.remove('selected-item');
      that?.classList.remove('button-primary-option-2');
      that?.classList.add('button-secondary');
      return;
    }

    event.stopPropagation();

    const productHandle = that?.dataset.productHandle;
    if (productHandle) {
      this.openPopup(productHandle, that);
    }
  }

  /**
   * Opens the product detail popup.
   * Fetches section content via section rendering API.
   *
   * @param {string} productHandle - The product handle for the URL
   * @param {HTMLElement} triggerButton - The select button that triggered the popup
   */
  async openPopup(productHandle, triggerButton) {
    this.currentSelectButton = triggerButton;
    this.popupOverlay?.classList.add('active');
    document.body.style.overflow = 'hidden';

    this.#positionPopupOverCard(triggerButton);

    const variantId = triggerButton?.dataset.variantId;
    let url = `/products/${productHandle}?section_id=product-set-popup`;
    if (variantId) {
      url += `&variant=${variantId}`;
    }

    try {
      const response = await fetch(url);
      const html = await response.text();

      if (this.popupBody) {
        this.popupBody.innerHTML = html;
        this.#initPopupContent();
      }
    } catch (error) {
      console.error('Failed to load product popup:', error);
      this.closePopup();
    }
  }

  /**
   * Positions the popup dialog directly over the clicked product card (desktop only).
   * Clamps the position so the popup stays within the viewport.
   */
  #positionPopupOverCard(triggerButton) {
    const dialog = this.querySelector('.product-set-popup-dialog');
    if (!dialog || !triggerButton) return;

    if (window.innerWidth <= 768) {
      this.#resetDialogPosition(dialog);
      return;
    }

    const card = triggerButton.closest('.product-set-item');
    if (!card) return;

    const cardRect = card.getBoundingClientRect();

    dialog.style.left = `${cardRect.left}px`;
    dialog.style.width = `${cardRect.width}px`;
  }

  #resetDialogPosition(dialog) {
    if (!dialog) return;
    dialog.style.left = '';
    dialog.style.width = '';
  }

  /**
   * Closes the popup and clears its content.
   */
  closePopup() {
    this.popupOverlay?.classList.remove('active');
    document.body.style.overflow = '';

    this.#resetDialogPosition(this.querySelector('.product-set-popup-dialog'));

    if (this.popupBody) {
      this.popupBody.innerHTML = '';
    }
    this.currentSelectButton = null;
  }

  /**
   * Closes popup when clicking on the backdrop overlay.
   */
  #handleOverlayClick(event) {
    if (event.target === this.popupOverlay) {
      this.closePopup();
    }
  }

  /**
   * Closes popup on Escape key press.
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.popupOverlay?.classList.contains('active')) {
      this.closePopup();
    }
  }

  /**
   * Initializes event listeners for popup content after it is loaded.
   */
  #initPopupContent() {
    const popupContent = this.popupBody?.querySelector('.product-set-popup-content');
    if (!popupContent) return;

    popupContent.addEventListener('variant:update', (event) => {
      event.stopPropagation();

      const variant = event.detail?.resource;
      if (!variant?.id) return;

      const confirmBtn = popupContent.querySelector('.popup-confirm-selection-btn');
      if (confirmBtn) {
        confirmBtn.dataset.variantId = variant.id;
        if (variant.available === false) {
          confirmBtn.setAttribute('disabled', 'true');
        } else {
          confirmBtn.removeAttribute('disabled');
        }
      }

      popupContent.dataset.variantId = variant.id;

      const sourceHtml = event.detail?.data?.html;
      const productId = event.detail?.data?.productId;
      if (sourceHtml && productId) {
        const newPriceHtml = sourceHtml.querySelector(`product-price[data-product-id="${productId}"]`)?.innerHTML;
        const popupPrice = popupContent.querySelector(`product-price[data-product-id="${productId}"]`);
        if (newPriceHtml && popupPrice) {
          popupPrice.innerHTML = newPriceHtml;
        }
      }

      const featuredMediaId = variant.featured_media?.id;
      const slideshow = popupContent.querySelector('slideshow-component');
      if (slideshow && featuredMediaId) {
        const slides = slideshow.querySelectorAll('slideshow-slide');
        for (const slide of slides) {
          if (slide.getAttribute('variant-image') == null) continue;
          slide.hidden = slide.getAttribute('slide-id') != featuredMediaId;
        }
        if (typeof slideshow.select === 'function') {
          slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
        }
      }
    });

    const confirmBtn = popupContent.querySelector('.popup-confirm-selection-btn');
    confirmBtn?.addEventListener('click', () => this.#handleConfirmSelection());
  }

  /**
   * Handles the "CONFIRM SELECTION" button click in the popup.
   * Adds the selected variant to the bundle, syncs the variant
   * back to the product card, and closes the popup.
   */
  #handleConfirmSelection() {
    const popupContent = this.popupBody?.querySelector('.product-set-popup-content');
    if (!popupContent) return;

    const confirmBtn = popupContent.querySelector('.popup-confirm-selection-btn');
    const variantId = Number(confirmBtn?.dataset.variantId);
    const productId = Number(confirmBtn?.dataset.productId);

    if (!variantId) return;

    const productJsonEl = popupContent.querySelector('.popup-product-json');
    if (!productJsonEl) return;

    let productJson;
    try {
      productJson = JSON.parse(productJsonEl.textContent);
    } catch {
      console.error('Failed to parse product JSON');
      return;
    }

    const existingVariant = this.selectedVariants.find(
      v => v.product.id === productId
    );
    if (existingVariant) {
      this.removeVariant(existingVariant.variantId);
    }

    this.addVariant(variantId, productJson);

    if (this.currentSelectButton) {
      this.currentSelectButton.classList.add('selected-item', 'button-primary-option-2');
      this.currentSelectButton.classList.remove('button-secondary');

      this.#syncVariantToCard(this.currentSelectButton, variantId, productJson);
    }

    this.closePopup();
  }

  /**
   * Syncs the selected variant from the popup back to the product card.
   * Updates the card image, swatch checked state, and select button variant ID.
   *
   * @param {HTMLElement} selectButton - The product card's select button
   * @param {number} variantId - The confirmed variant ID
   * @param {Object} productJson - The full product JSON object
   */
  #syncVariantToCard(selectButton, variantId, productJson) {
    const productSetItem = selectButton.closest('.product-set-item');
    if (!productSetItem) return;

    const variant = productJson.variants.find(v => v.id === variantId);
    if (!variant) return;

    selectButton.dataset.variantId = String(variantId);
    if (variant.available === false) {
      selectButton.setAttribute('disabled', 'true');
    } else {
      selectButton.removeAttribute('disabled');
    }

    const cardImage = productSetItem.querySelector('.product-set-image__img');
    if (cardImage && variant.featured_image?.src) {
      cardImage.src = variant.featured_image.src;
      cardImage.srcset = '';
    }

    const swatchPicker = productSetItem.querySelector('swatches-variant-picker-component');
    if (swatchPicker) {
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      const fieldsets = swatchPicker.querySelectorAll('fieldset');

      fieldsets.forEach(fieldset => {
        const inputs = fieldset.querySelectorAll('input[type="radio"]');
        inputs.forEach(input => {
          input.checked = variantOptions.includes(input.value);
        });
      });
    }
  }

  /**
   * Add variant to selected bundle list
   *
   * @param {number} variantId
   * @param {Object} product
   */
  addVariant(variantId, product) {

    variantId = Number(variantId);

    const exists = this.selectedVariants.some(v => v.variantId === variantId);

    if (!exists) {
      this.selectedVariants.push({
        variantId,
        product
      });
    }

    this.updateThreshold();
    this.updateBundlePrice();
    this.updateAddToCartButton();
  }

  /**
   * Remove variant from bundle
   *
   * @param {number} variantId
   */
  removeVariant(variantId) {

    variantId = Number(variantId);

    this.selectedVariants = this.selectedVariants.filter(
      item => item.variantId !== variantId
    );

    this.updateThreshold();
    this.updateBundlePrice();
    this.updateAddToCartButton();
  }

  /**
   * Create bundle and add all selected variants to cart
   */
  async createBundle() {

    if (!this.selectedVariants.length) return;

    this.addToCartButton?.setAttribute('disabled', 'true');

    const bundleId = Date.now();

    const items = this.selectedVariants.map(item => ({
      id: item.variantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _sell_type: 'upsell'
      }
    }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map(el => el.dataset.sectionId)
      .filter(Boolean);

    const payload = { items };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
    }

    try {

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent({}, '', {
          itemCount: cart.item_count,
          sections: data.sections || {},
        })
      );

      this.#showAddedState();
      this.#openCartDrawer();

      this.selectedVariants = [];
      this.#resetSelectButtons();
      this.updateThreshold();
      this.updateBundlePrice();
      this.updateAddToCartButton();

    } catch (error) {
      console.error('Bundle add error:', error);
      this.addToCartButton?.removeAttribute('disabled');
    }
  }

  #showAddedState() {
    this.querySelector('.add-to-cart-text')?.classList.add('hidden');
    this.querySelector('.add-to-cart-text--added')?.classList.remove('hidden');

    setTimeout(() => {
      this.querySelector('.add-to-cart-text--added')?.classList.add('hidden');
      this.updateAddToCartButton();
    }, 2000);
  }

  #openCartDrawer() {
    /** @type {HTMLElement & { showDialog?: () => void } | null} */
    const cartDrawer = document.querySelector("cart-drawer-component");
    if (cartDrawer?.showDialog) {
      cartDrawer.showDialog();
    }
  }

  /**
   * Update threshold progress UI
   */
  updateThreshold() {

    let count = this.selectedVariants.length;
    const threshold = this.threshold;

    if (count > threshold) {
      count = threshold;
    }

    if (this.thresholdText) {
      this.thresholdText.textContent = `${count}/${threshold}`;
    }

    if (this.thresholdMessage) {
      if (count >= threshold && this.successMessage) {
        this.thresholdMessage.textContent = this.successMessage;
      } else if (this.messageTemplate) {
        const remaining = Math.max(threshold - count, 0);
        this.thresholdMessage.textContent = interpolate(this.messageTemplate, { remaining });
        if (this.discountLabel && this.discountLabelTemplate) {
          this.discountLabel.textContent = interpolate(this.discountLabelTemplate, { remaining });
        }
      }
    }

    const bars = this.querySelectorAll('.threshold-bar');

    bars.forEach((bar, index) => {

      if (index < count) {
        bar.classList.add('active');
      } else {
        bar.classList.remove('active');
      }

    });
  }

  /**
   * Calculate and update total bundle price
   */
  updateBundlePrice() {

    let total = 0;

    this.selectedVariants.forEach(item => {

      let variant = item.product.variants.find(
        v => v.id === item.variantId
      );

      if (variant) {
        total += variant.price;
      }

    });

    const formatted = Shopify.formatMoney(total, window.moneyFormat);

    this.setPrice.textContent = formatted;
  }

  /**
   * Handle add to cart button click
   */
  #handleAddToCartClick() {
    this.createBundle();
  }

  /**
   * Enable / Disable add to cart button
   */
  updateAddToCartButton() {

    if (!this.addToCartButton) return;

    if (this.selectItemsText && this.selectTextTemplate) {
      this.selectItemsText.textContent = this.selectTextTemplate.replace('$$', this.threshold);
    }

    const hasSelection = this.selectedVariants.length > 0;

    if (hasSelection) {
      this.addToCartButton.removeAttribute('disabled');
      this.addToCartButton.classList.remove('disabled');
      this.querySelector('.select-items-text')?.classList.add('hidden');
      this.querySelector('.add-to-cart-text')?.classList.remove('hidden');
    } else {
      this.addToCartButton.setAttribute('disabled', 'true');
      this.addToCartButton.classList.add('disabled');
      this.querySelector('.select-items-text')?.classList.remove('hidden');
      this.querySelector('.add-to-cart-text')?.classList.add('hidden');
    }
  }
}

if (!customElements.get('product-set')) {
  customElements.define('product-set', ProductSet);
}
